









































































import { Component, Vue, Prop } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import dayjs from 'dayjs'
import InfiniteLoading, { StateChanger } from 'vue-infinite-loading'
import { getGender } from '@/utils/caption'
import { formatJpDate } from '@/utils/datetime'
import { ScheduleState } from '@/store/schedule/types'
import { ScheduleCommentState } from '@/store/scheduleComment/types'
import CommentForm, { FormPayload } from '@/components/comments/CommentForm.vue'
import { CreateCommentPayload } from '@/api/scheduleComment/types'
import { EventEmitter } from 'events'
import { DeleteConfirmResults } from '@/constants/utils'

type ScheduleCommentItem = (ScheduleState | ScheduleCommentState) & {
  itemType: number
}
type ScheculeCommentList = Array<ScheduleCommentItem>
type PaginationEventType = 1 | 2

const REACH_EVENT_TYPE_TOP = 1
const REACH_EVENT_TYPE_BOTTOM = 2
const CONFIRM_DELETE = 3

const EVENT = {
  CLICK: 'click',
  WHEEL: 'wheel'
}

const isPaginationEventType = (
  eventType: number
): eventType is PaginationEventType =>
  [REACH_EVENT_TYPE_TOP, REACH_EVENT_TYPE_BOTTOM].indexOf(eventType) !== -1

@Component({
  components: {
    InfiniteLoading: InfiniteLoading,
    Loading: () => import('@/components/common/loadings/Index.vue'),
    Schedule: () => import('@/components/comments/Schedule.vue'),
    Comment: () => import('@/components/comments/Comment.vue'),
    AlertModal: () => import('@/components/common/modals/AlertModal.vue'),
    CommentForm: CommentForm
  }
})
export default class ScheduleComment extends Vue {
  @Prop(Number) readonly patientId!: number
  @Prop(Number) readonly scheduleId!: number | undefined

  readonly HEADER_HEIGHT = 80
  readonly REACH_EVENT_TYPE_TOP = REACH_EVENT_TYPE_TOP
  readonly REACH_EVENT_TYPE_BOTTOM = REACH_EVENT_TYPE_BOTTOM

  readonly ITEM_TYPE_SCHEDULE = 1
  readonly ITEM_TYPE_COMMENT = 2
  readonly ITEM_TYPE_REPLY = 3
  readonly itemType2Class = {
    [this.ITEM_TYPE_SCHEDULE]: 'part01',
    [this.ITEM_TYPE_COMMENT]: 'part02',
    [this.ITEM_TYPE_REPLY]: 'part03'
  }

  $store!: ExStore
  events = new EventEmitter()
  showDeleteConfirmModal = false
  pennding = true
  activeReplayFormToken: string | null = null
  showLoadings = {
    [this.REACH_EVENT_TYPE_TOP]: false,
    [this.REACH_EVENT_TYPE_BOTTOM]: false
  }

  isSettingFirstView = false
  didInteractAfterUpdate = false
  isFixedFirstView = false

  get list(): ScheculeCommentList {
    const list = this.scheduleIds.reduceRight((list, id) => {
      const schedule = this.schedules[id]
      const relation = this.scheduleCommentRelation[id]

      return list.concat([
        {
          ...schedule,
          itemType: this.ITEM_TYPE_SCHEDULE
        },
        ...relation.map(commentId => {
          const comment = this.comments[commentId]

          return {
            ...comment,
            itemType: !comment.parentComment
              ? this.ITEM_TYPE_COMMENT
              : this.ITEM_TYPE_REPLY
          }
        })
      ])
    }, [] as ScheculeCommentList)

    return list
  }

  get schedules() {
    return this.$store.getters['schedule/schedules']
  }

  get scheduleIds() {
    return this.$store.getters['schedule/sort'] || []
  }

  get scheduleCommentRelation() {
    return this.$store.getters['scheduleComment/scheduleCommentRelation']
  }

  get comments() {
    return this.$store.getters['scheduleComment/comments']
  }

  get totalCount() {
    return this.$store.getters['schedule/schedules'] || 0
  }

  get patient() {
    return this.$store.getters['patient/patients'][this.patientId] || null
  }

  get patientFullName() {
    return this.patient ? this.patient.lastName + this.patient.firstName : ''
  }

  get patientBirthDate() {
    if (!this.patient || !this.patient.birthDate) {
      return ''
    }
    const date = dayjs(this.patient.birthDate)
    return formatJpDate(date.toDate())
  }

  get patientGender() {
    return this.patient ? getGender(this.patient.gender) : ''
  }

  makeItemToken(itemType: number, itemId: number) {
    return `${itemType}_${itemId}`
  }

  isActiveReplyForm(itemType: number, itemId: number) {
    return this.makeItemToken(itemType, itemId) === this.activeReplayFormToken
  }

  handleDeleteConfirmOk() {
    this.events.emit(CONFIRM_DELETE, DeleteConfirmResults.OK)
  }

  handleDeleteConfirmCancel() {
    this.events.emit(CONFIRM_DELETE, DeleteConfirmResults.CANCEL)
  }

  async handleDeleteSchedule(scheduleId: number) {
    try {
      this.showDeleteConfirmModal = true
      const resust = await new Promise<DeleteConfirmResults>(resolve =>
        this.events.once(CONFIRM_DELETE, resolve)
      )
      this.showDeleteConfirmModal = false
      if (resust === DeleteConfirmResults.CANCEL) {
        return
      }
      await this.$store.dispatch('schedule/delete', { scheduleId })
    } catch (error) {
      console.error(error)
    } finally {
      this.showDeleteConfirmModal = false
    }
  }

  handleClickReply(itemType: number, itemId: number) {
    this.activeReplayFormToken = this.makeItemToken(itemType, itemId)
  }

  handleCommentSubmit(index: number, formParams: FormPayload) {
    this.activeReplayFormToken = null
    const params = this.extractCreateCommentPayload(
      this.list[index],
      formParams
    )
    this.$store.dispatch('scheduleComment/create', params)
  }

  async handleReachEnd(eventType: number, $state: StateChanger) {
    if (!isPaginationEventType(eventType)) {
      throw new Error('An invalid arugument is passed into handleReachEnd')
    }

    try {
      if (!this.isFixedFirstView) {
        $state.loaded()
        return
      }

      if (!this.hasNext(eventType)) {
        $state.complete()
        return
      }

      this.showLoadings[eventType] = true

      await this.loadPage(eventType)

      $state.loaded()
    } catch (err) {
      $state.complete()
    } finally {
      this.showLoadings[eventType] = false
    }
  }

  hasNext(eventType: number) {
    switch (eventType) {
      case this.REACH_EVENT_TYPE_TOP:
        return this.$store.getters['scheduleComment/hasNext']

      case this.REACH_EVENT_TYPE_BOTTOM:
        return this.$store.getters['scheduleComment/hasPrev']

      default:
        throw new Error('An invalid arugument is passed into hasNext')
    }
  }

  async loadPage(eventType: number) {
    switch (eventType) {
      case this.REACH_EVENT_TYPE_TOP:
        await this.$store.dispatch('scheduleComment/paginateNext')
        if (!this.didInteractAfterUpdate) {
          this.isSettingFirstView = false
          this.fixFirstScrollPosition()
        }
        break

      case this.REACH_EVENT_TYPE_BOTTOM:
        await this.$store.dispatch('scheduleComment/paginatePrev')
        break

      default:
        throw new Error('An invalid arugument is passed into loadPage')
    }
  }

  extractCreateCommentPayload(
    parentItem: ScheduleCommentItem,
    params: FormPayload
  ): CreateCommentPayload {
    const content = params.content

    switch (parentItem.itemType) {
      case this.ITEM_TYPE_SCHEDULE:
        return {
          parentComment: null,
          event: parentItem.id,
          content
        }
      case this.ITEM_TYPE_COMMENT:
        return {
          parentComment: parentItem.id,
          event: (parentItem as ScheduleCommentState).event,
          content
        }
      default:
        throw new Error(
          'An invalid arugument is passed into extractCreateCommentPayload'
        )
    }
  }

  fixFirstScrollPosition() {
    if (this.isSettingFirstView) {
      return
    }

    const schedules = this.$refs.schedules as Vue[] | undefined
    const length = (schedules || []).length

    if (length < this.scheduleIds.length) {
      return
    }

    this.isSettingFirstView = true

    window.addEventListener(EVENT.CLICK, this.onClickAfterUpdate)
    window.addEventListener(EVENT.WHEEL, this.onWheelAfterUpdate)

    setTimeout(() => {
      const targetIndex = schedules?.findIndex(
        (item: any) => item.schedule.id == this.scheduleId
      )
      const index =
        targetIndex != undefined && targetIndex >= 0 ? targetIndex : length - 1
      const rect = schedules![index].$el.getBoundingClientRect()

      const y = window.scrollY + rect.top - this.HEADER_HEIGHT
      window.scrollTo(0, y)

      this.isFixedFirstView = true
    }, 200)
  }

  created() {
    this.$store.commit('schedule/clear')
    this.$store.commit('scheduleComment/clear')
    Promise.all([
      this.$store.dispatch('scheduleComment/load', {
        patientId: this.patientId,
        scheduleId: this.scheduleId
      }),
      this.$store.dispatch('worker/updateList', {
        patientIds: [this.patientId]
      })
    ]).then(() => {
      this.pennding = false
    })
  }

  onClickAfterUpdate() {
    if (!this.showLoadings[REACH_EVENT_TYPE_TOP]) {
      this.didInteractAfterUpdate = true
      window.removeEventListener(EVENT.CLICK, this.onClickAfterUpdate)
    }
  }

  onWheelAfterUpdate() {
    if (!this.showLoadings[REACH_EVENT_TYPE_TOP]) {
      this.didInteractAfterUpdate = true
      window.removeEventListener(EVENT.WHEEL, this.onWheelAfterUpdate)
    }
  }

  updated() {
    this.fixFirstScrollPosition()
  }

  beforeDestroy() {
    window.removeEventListener(EVENT.CLICK, this.onClickAfterUpdate)
    window.removeEventListener(EVENT.WHEEL, this.onWheelAfterUpdate)
  }
}
